// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/CaughtUpClub.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiYoutube,
  FiMusic,
  FiHeadphones,
  FiPlay,
  FiInstagram,
  FiGlobe,
} from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const PartyBox = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "🎉";
    position: absolute;
    top: -20px;
    right: -20px;
    font-size: 100px;
    opacity: 0.1;
  }
`;

const MusicStylesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${(props) => props.theme.spacing.md};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const MusicStyleCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing.lg};
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  color: ${(props) => props.theme.colors.homeGold};
  border: 2px solid transparent;
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    background: ${(props) => props.theme.colors.accent}20;
    border-color: ${(props) => props.theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${(props) => props.theme.colors.accent};
    font-size: ${(props) => props.theme.fontSizes.xl};
  }
`;

const KeywordBadge = styled.div`
  display: inline-block;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.lg};
  border-radius: 25px;
  font-size: ${(props) => props.theme.fontSizes.lg};
  font-weight: 700;
  margin: ${(props) => props.theme.spacing.lg} 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    position: relative;
    padding-left: ${(props) => props.theme.spacing["2xl"]};
    margin-bottom: ${(props) => props.theme.spacing.md};
    color: ${(props) => props.theme.colors.homeText};

    &::before {
      content: "✨";
      position: absolute;
      left: 0;
      top: 0;
    }
  }
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const CaughtUpClub = () => {
  const musicStyles = [
    "Club",
    "EDM",
    "Trap",
    "World Beat",
    "Gospel",
    "Ethereal",
    "Afro Beat",
    "CCM",
    "Hip-Hop",
    "Remix",
    "Dance",
    "Pop",
  ];

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>The Caught-Up Club - The Music Church</title>
        <meta
          name="description"
          content="The Caught-Up Club - Audio/visual content creation celebrating in anticipation of His return. A wide mix of music promoting the ways of God to youth culture."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              The Caught-Up Club
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Where faith meets the beat
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/caught-up-club-mirrored-recess-final.png"
          alt="The Caught-Up Club - Audio/visual content creation"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About The Caught-Up Club</SectionTitle>
                <Text>
                  The Caught-Up Club is our dynamic audio/visual content
                  creation ministry for YouTube, social media, and equinox
                  virtual reality systems. We create a wide mix of extended play
                  music in remix DJ mode, featuring various styles that speak to
                  today's generation.
                </Text>

                <KeywordBadge>🎉 "Start the Party" 🎉</KeywordBadge>

                <Text>
                  This ministry is in shallow waters, not deep, reaching out
                  mainly to the youthful pop culture of our age, offering an
                  alternative to darkness. We use modern beats and sounds to
                  share easy examples of truth that He left for us to follow.
                </Text>
              </Section>

              <PartyBox
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                  We Rejoice in Anticipation!
                </h3>
                <p style={{ fontSize: "1.125rem", lineHeight: "1.8" }}>
                  Celebrating His glorious return through music that moves the
                  soul and body
                </p>
              </PartyBox>

              <Section>
                <SectionTitle>Our Music Styles</SectionTitle>
                <Text>
                  We blend various genres to create a unique sound that
                  resonates with modern audiences while carrying eternal truths:
                </Text>
                <MusicStylesGrid>
                  {musicStyles.map((style, index) => (
                    <MusicStyleCard
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {style}
                    </MusicStyleCard>
                  ))}
                </MusicStylesGrid>
              </Section>

              <QuoteBox>
                "Rejoice in The Lord Always. Again I say rejoice!"
                <br />- Philippians 4:4
              </QuoteBox>

              <Section>
                <SectionTitle>What Makes Us Different</SectionTitle>
                <FeatureList>
                  <li>
                    Multiple segues creating continuous worship experiences
                  </li>
                  <li>Modern production techniques with spiritual messages</li>
                  <li>Content designed for social media generation</li>
                  <li>Virtual reality experiences for immersive worship</li>
                  <li>Collaborations with artists worldwide</li>
                  <li>Fresh takes on traditional hymns and worship songs</li>
                </FeatureList>
              </Section>

              <Section>
                <SectionTitle>Our Mission</SectionTitle>
                <Text>
                  We believe worship doesn't have to be boring or outdated. The
                  Caught-Up Club creates content that makes young people want to
                  press play, dance, and share with their friends - all while
                  encountering the truth of God's love.
                </Text>
                <Text>
                  Our productions use the language of modern music to
                  communicate timeless truths, meeting people where they are and
                  lifting them up to where God wants them to be.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Find Us Online
                </SectionTitle>
                <InfoItem>
                  <FiYoutube />
                  <span>YouTube Channel</span>
                </InfoItem>
                <InfoItem>
                  <FiInstagram />
                  <span>Instagram: @caughtupclub</span>
                </InfoItem>
                <InfoItem>
                  <FiMusic />
                  <span>Spotify Playlists</span>
                </InfoItem>
                <InfoItem>
                  <FiGlobe />
                  <span>VR Experiences</span>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Latest Releases
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  Check out our newest content:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>"Heaven's Beat" - EDM Worship Mix</li>
                  <li>"Caught Up" - Afrobeat Praise</li>
                  <li>"Glory Trap" - Hip-Hop Devotional</li>
                  <li>"Eternal Dance" - House Worship Set</li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Join the Movement
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  Are you a producer, DJ, or content creator? We're always
                  looking for collaborators who share our vision of reaching
                  youth culture with the Gospel.
                </Text>
                <Button
                  variant="accent"
                  fullWidth
                  style={{ marginTop: "1rem" }}
                  icon={<FiHeadphones />}
                >
                  Collaborate With Us
                </Button>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Ready to Start the Party?
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Subscribe to our channels and join thousands celebrating His
              return through music that moves!
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                icon={<FiYoutube />}
                onClick={() => console.log("YouTube clicked")}
              >
                Subscribe on YouTube
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                icon={<FiPlay />}
                onClick={() => console.log("Listen clicked")}
              >
                Listen Now
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default CaughtUpClub;
